// analytics/hooks/useAnalytics.js
import log from 'loglevel';
import { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { 
  trackPageHit,
  updateTimeOnPageDebounced,
  setCampaignData, 
  setPageStartTime,
  trackCustomEvent,
  trackUserAction,
  trackError,
  trackPerformance,
  trackRouteChange,
  updateCampaignFromUrl,
  initializeAnalytics,
  cleanupAnalytics,
} from 'Store/Analytics/Actions';
import { parseUrlParams, debounce, throttle } from 'analytics/utils/utils';

export const useAnalytics = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  
  // Analytics state from Redux
  const analytics = useSelector(state => state.analytics || {});
  const userInfo = useSelector(state => state.userInfo || {});
  
  // Refs for tracking state
  const previousLocation = useRef(location);
  const isInitialized = useRef(false);
  const trackingInProgress = useRef(false);
  const performanceMarks = useRef({});
  
  // Destructure analytics state for easier access
  const {
    sessionId,
    fingerprint,
    currentHitId,
    pageStartTime,
    campaignId,
    utmData,
    isTracking,
    error,
    initialized,
    config = {},
  } = analytics;

  // ====== CORE TRACKING FUNCTIONS ======

  // Enhanced page tracking with duplicate prevention
  const trackPage = useCallback(async (customData = {}) => {
    if (trackingInProgress.current || !config.enableTracking) {
      log.log('Analytics: Tracking skipped (in progress or disabled)');
      return;
    }

    try {
      trackingInProgress.current = true;
      
      const pageData = {
        url: window.location.href,
        title: document.title,
        timestamp: new Date().toISOString(),
        user_mode: userInfo.mode,
        isAuthenticated: !!userInfo.token,
        ...customData,
      };

      const result = await dispatch(trackPageHit(pageData));
      dispatch(setPageStartTime());
      
      log.log('Analytics: Page tracked successfully', result);
      return result;
    } catch (error) {
      log.error('Analytics: Page tracking failed', error);
      if (config.enableErrorTracking) {
        dispatch(trackError(error, { context: 'page_tracking' }));
      }
    } finally {
      trackingInProgress.current = false;
    }
  }, [dispatch, userInfo, config, trackingInProgress]);

  // Update campaign data from URL
  const updateCampaign = useCallback(() => {
    dispatch(updateCampaignFromUrl());
  }, [dispatch]);

  // Update time on current page
  const updateCurrentPageTime = useCallback(() => {
    if (currentHitId && pageStartTime) {
      const timeOnPage = Math.floor((Date.now() - pageStartTime) / 1000);
      log.log(`Analytics: Updating time on page: ${timeOnPage} seconds`);
      dispatch(updateTimeOnPageDebounced(currentHitId, timeOnPage));
    }
  }, [currentHitId, pageStartTime, dispatch]);

  // ====== SPECIALIZED TRACKING FUNCTIONS ======

  // Track route changes
  const trackRouteChangeEvent = useCallback((customData = {}) => {
    const fromPath = previousLocation.current?.pathname;
    const toPath = location.pathname;
    
    if (fromPath && fromPath !== toPath) {
      updateCurrentPageTime(); // Update time on previous page
      dispatch(trackRouteChange(fromPath, toPath, customData));
      previousLocation.current = location;
    }
  }, [location, dispatch, updateCurrentPageTime]);

  // Track custom events with validation
  const trackEvent = useCallback((eventName, eventData = {}) => {
    if (!eventName || typeof eventName !== 'string') {
      log.warn('Analytics: Invalid event name provided');
      return;
    }
    
    return dispatch(trackCustomEvent(eventName, {
      ...eventData,
      user_mode: userInfo.mode,
      isAuthenticated: !!userInfo.token,
    }));
  }, [dispatch, userInfo]);

  // Track user actions
  const trackAction = useCallback((action, additionalData = {}) => {
    if (!action) {
      log.warn('Analytics: No action provided');
      return;
    }
    
    return dispatch(trackUserAction(action, additionalData));
  }, [dispatch]);

  // Track errors with context
  const trackErrorEvent = useCallback((error, context = {}) => {
    if (!config.enableErrorTracking) return;
    
    return dispatch(trackError(error, {
      page_url: window.location.href,
      user_mode: userInfo.mode,
      ...context,
    }));
  }, [dispatch, config, userInfo]);

  // ====== PERFORMANCE TRACKING ======

  // Mark performance points
  const markPerformance = useCallback((label) => {
    try {
      const timestamp = performance.now();
      performanceMarks.current[label] = timestamp;
      performance.mark(`analytics-${label}`);
    } catch (error) {
      log.warn('Analytics: Performance marking failed', error);
    }
  }, []);

  // Measure performance between marks
  const measurePerformance = useCallback((startLabel, endLabel, customData = {}) => {
    try {
      const startTime = performanceMarks.current[startLabel];
      const endTime = performanceMarks.current[endLabel] || performance.now();
      
      if (startTime) {
        const duration = endTime - startTime;
        
        if (config.enablePerformanceTracking) {
          dispatch(trackPerformance({
            measurement: `${startLabel}_to_${endLabel}`,
            duration,
            start_time: startTime,
            end_time: endTime,
            ...customData,
          }));
        }
        
        // Clean up marks
        delete performanceMarks.current[startLabel];
        if (endLabel in performanceMarks.current) {
          delete performanceMarks.current[endLabel];
        }
        
        return duration;
      }
    } catch (error) {
      log.warn('Analytics: Performance measurement failed', error);
    }
  }, [dispatch, config]);

  // ====== LIFECYCLE FUNCTIONS ======

  // Initialize analytics
  const initialize = useCallback(async (initialData = {}) => {
    if (isInitialized.current) {
      log.log('Analytics: Already initialized');
      return;
    }

    try {
      await dispatch(initializeAnalytics(initialData));
      isInitialized.current = true;
    } catch (error) {
      log.error('Analytics: Initialization failed', error);
    }
  }, [dispatch]);

  // Cleanup analytics
  const cleanup = useCallback(() => {
    updateCurrentPageTime();
    dispatch(cleanupAnalytics());
  }, [updateCurrentPageTime, dispatch]);

  // ====== DEBOUNCED/THROTTLED FUNCTIONS ======

  // Debounced time update for frequent calls
  const debouncedTimeUpdate = useCallback(
    debounce(updateCurrentPageTime, config.debounceTime || 1000),
    [updateCurrentPageTime, config.debounceTime]
  );

  // Throttled scroll tracking
  const throttledScrollTrack = useCallback(
    throttle(() => {
      trackEvent('scroll', {
        scroll_y: window.scrollY,
        scroll_percent: Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100),
      });
    }, 5000),
    [trackEvent]
  );

  // ====== UTILITY FUNCTIONS ======

  // Check if analytics is ready
  const isReady = useCallback(() => {
    return initialized && fingerprint && !isTracking;
  }, [initialized, fingerprint, isTracking]);

  // Get current session info
  const getSessionInfo = useCallback(() => {
    return {
      sessionId,
      fingerprint,
      currentHitId,
      campaignId,
      utmData,
      timeOnCurrentPage: pageStartTime ? Math.floor((Date.now() - pageStartTime) / 1000) : 0,
    };
  }, [sessionId, fingerprint, currentHitId, campaignId, utmData, pageStartTime]);

  // Force refresh campaign data
  const refreshCampaignData = useCallback(() => {
    const campaignData = parseUrlParams();
    if (Object.keys(campaignData).some(key => campaignData[key])) {
      dispatch(setCampaignData(campaignData));
      return campaignData;
    }
    return null;
  }, [dispatch]);

  // ====== RETURN HOOK INTERFACE ======

  return {
    // Core tracking
    trackPage,
    updateCampaign,
    updateCurrentPageTime,
    
    // Specialized tracking
    trackRouteChange: trackRouteChangeEvent,
    trackEvent,
    trackAction,
    trackError: trackErrorEvent,
    
    // Performance tracking
    markPerformance,
    measurePerformance,
    
    // Lifecycle
    initialize,
    cleanup,
    
    // Debounced/throttled
    debouncedTimeUpdate,
    throttledScrollTrack,
    
    // Utilities
    isReady,
    getSessionInfo,
    refreshCampaignData,
    
    // State
    analytics,
    isTracking,
    error,
    initialized,
    
    // Data
    sessionId,
    fingerprint,
    currentHitId,
    campaignData: { campaignId, ...utmData },
  };
};